// Co-Parent Link — landing page for newly-signed-in users whose email or
// phone matches a guardian2* field on an existing user. App.js routes them
// here instead of /register so they can link to the existing family
// (skipping duplicate athlete creation) or decline and register fresh.
//
// Public route, but requires Firebase Auth (we identify the caller by uid).

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Box, Button, CircularProgress, Stack, Typography, Alert, Paper,
} from '@mui/material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';

const gold = '#C9A84C';
const dark = '#0D1E35';
const goldMid = 'rgba(201,168,76,0.6)';

export default function CoParentLink() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guardian1Uid = searchParams.get('match');

  const [loading, setLoading] = useState(true);
  const [guardian1, setGuardian1] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Link to Family · Torch Beach Volleyball';
    let cancelled = false;
    (async () => {
      try {
        if (!guardian1Uid) {
          setError('No family match specified. Please continue with normal registration.');
          setLoading(false);
          return;
        }
        // Load guardian1's user doc (for display only — full verification
        // happens server-side when we call linkCoParent).
        const g1Snap = await getDoc(doc(db, 'users', guardian1Uid));
        if (cancelled) return;
        if (!g1Snap.exists()) {
          setError('Family record not found.');
          setLoading(false);
          return;
        }
        const g1 = { id: g1Snap.id, ...g1Snap.data() };
        setGuardian1(g1);

        // Load their athletes (legacy + array). Display first names only for
        // confirmation; full data is loaded server-side during link.
        const [legacy, modern] = await Promise.all([
          getDocs(query(collection(db, 'athletes'), where('parentUserId', '==', guardian1Uid))),
          getDocs(query(collection(db, 'athletes'), where('parentUserIds', 'array-contains', guardian1Uid))),
        ]);
        const map = new Map();
        legacy.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
        modern.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
        const list = [...map.values()].filter(a => !a.isArchived && !a.mergedInto);
        setAthletes(list);
      } catch (e) {
        console.error('Co-parent load error:', e);
        setError('Something went wrong loading family info. Please continue with normal registration.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [guardian1Uid]);

  async function handleLink() {
    setLinking(true);
    setError('');
    try {
      const fns = getFunctions();
      const linkFn = httpsCallable(fns, 'linkCoParent');
      await linkFn({ guardian1Uid });
      // Navigate to Register with pre-fill data so they can confirm/edit their info.
      navigate(`/register?prefill=coparent&from=${guardian1Uid}`, { replace: true });
    } catch (e) {
      console.error('Link failed:', e);
      setError(e.message || 'Unable to link to this family. You can continue with normal registration.');
      setLinking(false);
    }
  }

  function handleDecline() {
    // Clear the prefill hint and go to normal Register
    navigate('/register', { replace: true });
  }

  if (loading) {
    return (
      <Box sx={{ background: dark, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: gold }} />
      </Box>
    );
  }

  if (error && !guardian1) {
    return (
      <Box sx={{ background: dark, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 480 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
          <Button variant="contained" onClick={() => navigate('/register', { replace: true })}
            sx={{ background: gold, color: dark, '&:hover': { background: '#e0be6a' } }}>
            Continue to Registration
          </Button>
        </Box>
      </Box>
    );
  }

  const familyLastName = guardian1?.lastName || '';
  const guardian1Display = `${guardian1?.firstName || ''} ${guardian1?.lastName || ''}`.trim();

  return (
    <Box sx={{
      background: dark, minHeight: '100vh', color: 'white',
      fontFamily: '"Barlow", sans-serif',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '36px 16px 56px',
    }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4, mt: { xs: 2, sm: 5 } }}>
        <FamilyRestroomIcon sx={{ fontSize: 64, color: gold, mb: 1 }} />
        <Box sx={{
          fontFamily: '"Bebas Neue", sans-serif',
          fontSize: 'clamp(1.8rem,5vw,2.6rem)',
          letterSpacing: '3px', lineHeight: 1.1,
        }}>
          Welcome back
        </Box>
        <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', maxWidth: 460 }}>
          It looks like you may be the co-parent of an existing family at Torch Beach Volleyball.
        </Typography>
        <Box sx={{
          width: 60, height: 2, mx: 'auto', mt: 2,
          background: `linear-gradient(90deg, transparent, ${gold}, transparent)`,
        }} />
      </Box>

      {/* Family preview */}
      <Paper elevation={0} sx={{
        width: '100%', maxWidth: 480,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(201,168,76,0.25)',
        borderRadius: '14px',
        p: { xs: 3, sm: 4 },
        mb: 3,
      }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ fontSize: '0.72rem', color: goldMid, letterSpacing: '2px', textTransform: 'uppercase', mb: 0.5 }}>
              Family
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', color: gold, fontFamily: '"Bebas Neue", sans-serif', letterSpacing: '2px' }}>
              The {familyLastName} family
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
              Listed by {guardian1Display}
            </Typography>
          </Box>

          {athletes.length > 0 ? (
            <Box>
              <Typography sx={{ fontSize: '0.72rem', color: goldMid, letterSpacing: '2px', textTransform: 'uppercase', mb: 1 }}>
                Athletes ({athletes.length})
              </Typography>
              <Stack spacing={1}>
                {athletes.map(a => (
                  <Box key={a.id} sx={{
                    p: 1.5, borderRadius: 1.5,
                    background: 'rgba(0,0,0,0.2)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}>
                    <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
                      {a.firstName} {a.lastName?.charAt(0) || ''}.
                    </Typography>
                    {a.athleteType && (
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
                        {a.athleteType} {a.skillGroup ? `· ${a.skillGroup}` : ''}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', fontStyle: 'italic' }}>
              This family hasn't added any athletes yet, but you can still link to their account.
            </Typography>
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ width: '100%', maxWidth: 480, mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* CTAs */}
      <Stack spacing={1.5} sx={{ width: '100%', maxWidth: 480 }}>
        <Button
          variant="contained"
          onClick={handleLink} disabled={linking}
          sx={{
            background: gold, color: dark, fontWeight: 700, py: 1.4,
            '&:hover': { background: '#e0be6a' },
          }}
        >
          {linking ? <CircularProgress size={22} sx={{ color: dark }} /> : `Yes, that's my family — link me`}
        </Button>
        <Button
          variant="outlined"
          onClick={handleDecline} disabled={linking}
          sx={{
            borderColor: goldMid, color: gold, py: 1.2,
            '&:hover': { borderColor: gold, background: 'rgba(201,168,76,0.08)' },
          }}
        >
          That's not me — register as a new parent
        </Button>
      </Stack>

      <Typography sx={{ mt: 4, fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', maxWidth: 460 }}>
        We matched your sign-in info to a co-parent contact someone added during their registration.
        Confirming will give you access to the listed athletes' check-ins, schedule, and account details.
      </Typography>
    </Box>
  );
}
